import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Loader2, Search, Clock, CheckCircle, XCircle,
  Eye, X, Download, User, MapPin, Calendar
} from 'lucide-react';
import { adminCorruptionAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const STATUS_CONFIG = {
  pending: { label: 'Jarayonda', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  accepted: { label: 'Qabul qilindi', color: '#22c55e', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rad qilindi', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
};

const CorruptionPage = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', admin_comment: '' });
  const [updating, setUpdating] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchReports();
  }, [filterStatus, currentPage]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (filterStatus) params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;
      
      const response = await adminCorruptionAPI.getAll(params);
      const data = response.data;
      setReports(data.reports || []);
      setTotalCount(data.count || data.reports?.length || 0);
      setStats(data.stats || null);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchReports();
  };

  const openDetail = (report) => {
    setSelectedReport(report);
    setUpdateData({ status: report.status, admin_comment: report.admin_comment || '' });
    setShowModal(true);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await adminCorruptionAPI.update(selectedReport.id, updateData);
      setShowModal(false);
      fetchReports();
    } catch (error) {
      alert('Xatolik yuz berdi');
    } finally {
      setUpdating(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  const exportToExcel = () => {
    const data = reports.map(r => ({
      'ID': r.id,
      'Sarlavha': r.title,
      'Tavsif': r.description,
      'Joylashuv': r.location || '-',
      'Ayblanuvchi': r.involved_person || '-',
      'Talaba': r.is_anonymous ? 'Anonim' : r.student_name,
      'Guruh': r.group_name || '-',
      'Holat': STATUS_CONFIG[r.status]?.label,
      'Admin izohi': r.admin_comment || '-',
      'Sana': new Date(r.created_at).toLocaleDateString('uz-UZ')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Korrupsiya xabarlari');
    XLSX.writeFile(wb, 'korrupsiya_xabarlari.xlsx');
  };

  const filteredReports = reports.filter(r => {
    const search = searchTerm.toLowerCase();
    return r.title.toLowerCase().includes(search) || 
           r.description.toLowerCase().includes(search) ||
           (r.involved_person && r.involved_person.toLowerCase().includes(search));
  });

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><AlertTriangle size={28} /> Korrupsiya xabarlari</h1>
          <p>Talabalardan kelgan xabarlar</p>
        </div>
        <button className="btn btn-success" onClick={exportToExcel} disabled={reports.length === 0}>
          <Download size={18} />
          Excel
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="corruption-stats">
          <div className="stat-card total">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Jami xabarlar</span>
          </div>
          <div className="stat-card pending">
            <Clock size={24} />
            <div>
              <span className="stat-number">{stats.pending}</span>
              <span className="stat-label">Jarayonda</span>
            </div>
          </div>
          <div className="stat-card accepted">
            <CheckCircle size={24} />
            <div>
              <span className="stat-number">{stats.accepted}</span>
              <span className="stat-label">Qabul qilindi</span>
            </div>
          </div>
          <div className="stat-card rejected">
            <XCircle size={24} />
            <div>
              <span className="stat-number">{stats.rejected}</span>
              <span className="stat-label">Rad qilindi</span>
            </div>
          </div>
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchReports()}
          />
        </div>
        <select 
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Barcha holatlar</option>
          <option value="pending">⏳ Jarayonda</option>
          <option value="accepted">✅ Qabul qilindi</option>
          <option value="rejected">❌ Rad qilindi</option>
        </select>
      </div>

      {/* Xabarlar ro'yxati */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="empty-state">
          <AlertTriangle size={48} />
          <p>Xabarlar topilmadi</p>
        </div>
      ) : (
        <div className="corruption-admin-list">
          {filteredReports.map(report => {
            const statusConfig = STATUS_CONFIG[report.status];
            const StatusIcon = statusConfig.icon;
            return (
              <div key={report.id} className="corruption-admin-card">
                <div className="card-header">
                  <div className="card-status" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                    <StatusIcon size={16} />
                    {statusConfig.label}
                  </div>
                  <span className="card-date">
                    <Calendar size={14} />
                    {new Date(report.created_at).toLocaleDateString('uz-UZ')}
                  </span>
                </div>
                <h3>{report.title}</h3>
                <p className="card-description">{report.description.substring(0, 200)}...</p>
                <div className="card-info">
                  <div className="info-item">
                    <User size={14} />
                    <span>{report.is_anonymous ? 'Anonim' : report.student_name}</span>
                  </div>
                  {report.location && (
                    <div className="info-item">
                      <MapPin size={14} />
                      <span>{report.location}</span>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => openDetail(report)}>
                    <Eye size={16} />
                    Ko'rish
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail & Update Modal */}
      {showModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Xabar #{selectedReport.id}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="report-detail-grid">
                <div className="detail-main">
                  <h3>{selectedReport.title}</h3>
                  <div className="detail-section">
                    <label>Tavsif:</label>
                    <p>{selectedReport.description}</p>
                  </div>
                  {selectedReport.location && (
                    <div className="detail-section">
                      <label>Joylashuv:</label>
                      <p>{selectedReport.location}</p>
                    </div>
                  )}
                  {selectedReport.involved_person && (
                    <div className="detail-section">
                      <label>Ayblanuvchi:</label>
                      <p>{selectedReport.involved_person}</p>
                    </div>
                  )}
                </div>
                <div className="detail-sidebar">
                  <div className="detail-info-card">
                    <h4>Ma'lumotlar</h4>
                    <div className="info-row">
                      <span>Talaba:</span>
                      <strong>{selectedReport.is_anonymous ? 'Anonim' : selectedReport.student_name}</strong>
                    </div>
                    {!selectedReport.is_anonymous && (
                      <>
                        <div className="info-row">
                          <span>ID:</span>
                          <strong>{selectedReport.student_id}</strong>
                        </div>
                        <div className="info-row">
                          <span>Guruh:</span>
                          <strong>{selectedReport.group_name || '-'}</strong>
                        </div>
                      </>
                    )}
                    <div className="info-row">
                      <span>Sana:</span>
                      <strong>{new Date(selectedReport.created_at).toLocaleString('uz-UZ')}</strong>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="update-section">
                <h4>Holatni yangilash</h4>
                <div className="status-buttons">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        className={`status-btn ${updateData.status === key ? 'active' : ''}`}
                        style={{ 
                          '--btn-color': config.color,
                          '--btn-bg': config.bg
                        }}
                        onClick={() => setUpdateData({...updateData, status: key})}
                      >
                        <Icon size={18} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
                <div className="form-group">
                  <label>Admin izohi</label>
                  <textarea
                    value={updateData.admin_comment}
                    onChange={(e) => setUpdateData({...updateData, admin_comment: e.target.value})}
                    placeholder="Izoh yozing..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Bekor qilish
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={updating}>
                {updating ? <Loader2 className="spinner" size={18} /> : <CheckCircle size={18} />}
                Saqlash
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorruptionPage;
